import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import {
  Badge,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  SvgIcon,
  Tab,
  Tabs,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { uniqBy } from "lodash";
import Flex from "../../../../components/Flex";
import { ScrollArea } from "../../../../components/ScrollBar";
import SideSheetDrawer from "../../../../components/SidesheetDrawer";
import Text from "../../../../components/Text";
import useCurrentUser from "../../../../hooks/local-storage/use-current-user";
import useDisclosure from "../../../../hooks/use-disclosure";
import { GetNotificationsCategoryEnum } from "../../../../services";
import { useGetPatientPortalNotifications } from "../../../../queries/notification/use-get-patient-portal-notifications";
import { useGetPatientPortalUnreadCounts } from "../../../../queries/notification/use-get-patient-portal-unread-counts";
import {
  usePatientPortalMarkAllRead,
  usePatientPortalMarkRead,
} from "../../../../queries/notification/use-patient-portal-mark-read";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  NOTIFICATION_COLORS,
} from "../../../../utils/utils";
import {
  formatDate,
  formatTimeAgo,
  isSameDay,
} from "../../../../utils/date-time-utility";

const CATEGORY_TABS = [
  { value: "", label: "All" },
  { value: "APPOINTMENT", label: "Appointment" },
  { value: "COMMUNICATION", label: "Communication" },
  { value: "CRITICAL", label: "Critical" },
  { value: "RESULTS", label: "Results" },
];

const CATEGORY_ICON_MAP: Record<string, React.ElementType> = {
  APPOINTMENT: CalendarMonthOutlinedIcon,
  CLINICAL: DescriptionOutlinedIcon,
  COMMUNICATION: ChatOutlinedIcon,
  CRITICAL: ErrorOutlineRoundedIcon,
  RESULTS: ScienceOutlinedIcon,
  MESSAGING: ChatOutlinedIcon,
};

const PatientPortalNotification = () => {
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [activeTab, setActiveTab] = useState<string>("");
  const disclosureHost = useDisclosure(false, {
    onClose: () => setActiveTab(""),
  });
  const [currentUser] = useCurrentUser();
  const patientUuid = currentUser?.uuid ?? "";

  const category = activeTab
    ? (activeTab as GetNotificationsCategoryEnum)
    : undefined;

  const {
    data: notificationData,
    refetch,
    isFetching,
  } = useGetPatientPortalNotifications({
    patientUuid,
    pagination,
    category,
  });

  const { data: unreadCountsData, refetch: refetchCounts } =
    useGetPatientPortalUnreadCounts(patientUuid);

  const { mutateAsync: markRead } = usePatientPortalMarkRead();
  const { mutateAsync: _markAllRead } = usePatientPortalMarkAllRead();

  const [notifications, setNotifications] = useState<any[]>([]);

  const unreadCounts: Record<string, number> =
    (unreadCountsData?.data as any) ?? {};
  const totalUnread =
    unreadCounts?.total ??
    Object.values(unreadCounts).reduce(
      (sum: number, v: any) => sum + (typeof v === "number" ? v : 0),
      0
    );

  useEffect(() => {
    setNotifications((prev) =>
      uniqBy(
        [...(prev ?? []), ...(notificationData?.data?.content ?? [])],
        "uuid"
      )
    );
  }, [notificationData]);

  const onRead = (notificationId: string, ind: number) => {
    setNotifications((prev) =>
      prev.toSpliced(ind, 1, { ...prev[ind], read: true })
    );
    markRead({ notificationUuid: notificationId })
      .then(() => {
        refetchCounts();
        refetch();
      })
      .catch(() => {
        setNotifications((prev) =>
          prev.toSpliced(ind, 1, { ...prev[ind], read: false })
        );
      });
  };

  const onTabChange = () => {
    setPagination(DEF_PAGINATION);
    setAtEnd(false);
    setNotifications([]);
  };

  const [atEnd, setAtEnd] = useState(false);

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement && !isFetching && !atEnd) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        if (scrollHeight - scrollTop - clientHeight <= 13) {
          setAtEnd(notificationData?.data?.content?.length === 0);
          setPagination((prev) => ({ ...prev, page: prev.page + 1 }));
        }
      }
    },
    [isFetching, pagination, atEnd, notificationData]
  );

  const groupedNotifications = groupByDate(notifications);

  return (
    <>
      <IconButton
        style={{ color: "#fff" }}
        onClick={() => {
          disclosureHost.open();
          refetch();
          refetchCounts();
        }}
      >
        <Badge
          badgeContent={totalUnread}
          max={9999}
          color="error"
          sx={{
            "& .MuiBadge-badge": {
              width: "20px",
              height: "20px",
              backgroundColor: NOTIFICATION_COLORS.BADGE_BG,
              color: NOTIFICATION_COLORS.BADGE_TEXT,
            },
          }}
        >
          <SvgIcon sx={{ fontSize: 24 }} viewBox="3 7.5 19 21">
            <path
              d="M21.7436 22.7013C21.1995 21.7641 20.3908 19.1124 20.3908 15.649C20.3908 13.5691 19.5645 11.5744 18.0938 10.1036C16.6231 8.63289 14.6283 7.80664 12.5484 7.80664C10.4685 7.80664 8.47373 8.63289 7.003 10.1036C5.53226 11.5744 4.70601 13.5691 4.70601 15.649C4.70601 19.1134 3.89629 21.7641 3.35222 22.7013C3.21328 22.9396 3.13963 23.2102 3.13868 23.486C3.13773 23.7618 3.20953 24.033 3.34683 24.2722C3.48413 24.5114 3.68207 24.7102 3.9207 24.8485C4.15933 24.9868 4.43021 25.0597 4.70601 25.0599H8.70661C8.88755 25.9453 9.36873 26.741 10.0688 27.3124C10.7688 27.8839 11.6447 28.196 12.5484 28.196C13.4521 28.196 14.328 27.8839 15.028 27.3124C15.7281 26.741 16.2093 25.9453 16.3902 25.0599H20.3908C20.6665 25.0595 20.9373 24.9865 21.1758 24.8481C21.4143 24.7098 21.6121 24.511 21.7492 24.2718C21.8864 24.0326 21.9581 23.7615 21.9571 23.4858C21.9562 23.2101 21.8825 22.9395 21.7436 22.7013ZM12.5484 26.6284C12.062 26.6282 11.5876 26.4773 11.1905 26.1964C10.7934 25.9156 10.4931 25.5185 10.331 25.0599H14.7658C14.6037 25.5185 14.3034 25.9156 13.9063 26.1964C13.5092 26.4773 13.0348 26.6282 12.5484 26.6284ZM4.70601 23.4914C5.46084 22.1935 6.27449 19.186 6.27449 15.649C6.27449 13.9851 6.93549 12.3893 8.11208 11.2127C9.28866 10.0361 10.8845 9.37512 12.5484 9.37512C14.2123 9.37512 15.8081 10.0361 16.9847 11.2127C18.1613 12.3893 18.8223 13.9851 18.8223 15.649C18.8223 19.183 19.634 22.1906 20.3908 23.4914H4.70601Z"
              fill="white"
            />
          </SvgIcon>
        </Badge>
      </IconButton>

      <SideSheetDrawer
        title="Notification"
        w="580px"
        open={disclosureHost.opened}
        onClose={disclosureHost.close}
        withCloseIcon
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            mx: -2,
            mt: -1,
          }}
        >
          {/* Tabs row — px:16, py:8, border top & bottom #E7E7E7 */}
          <Box
            sx={{
              borderTop: `1px solid ${NOTIFICATION_COLORS.BORDER}`,
              borderBottom: `1px solid ${NOTIFICATION_COLORS.BORDER}`,
              px: "16px",
              py: "8px",
              overflowX: "auto",
              overflowY: "clip",
            }}
          >
            <Tabs
              value={activeTab}
              variant="scrollable"
              scrollButtons={false}
              textColor="primary"
              indicatorColor="primary"
              sx={{
                minHeight: "29px",
                "& .MuiTabs-indicator": {
                  backgroundColor: NOTIFICATION_COLORS.TAB_INDICATOR,
                  height: "2px",
                },
                "& .MuiTabs-flexContainer": {
                  gap: "12px",
                  height: "29px",
                },
                "& .MuiButtonBase-root": {
                  padding: 0,
                  minWidth: 0,
                  minHeight: "29px",
                  paddingBottom: "6px",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                },
              }}
            >
              {CATEGORY_TABS.map((tab) => {
                const isActive = activeTab === tab.value;
                const count =
                  tab.value === ""
                    ? totalUnread
                    : (unreadCounts[tab.value?.toLowerCase()] ?? 0);
                return (
                  <Tab
                    key={tab.value}
                    value={tab.value}
                    onClick={() => {
                      onTabChange();
                      setActiveTab(tab.value);
                    }}
                    label={
                      <Flex gap="6px" alignItems="center">
                        <Text
                          fontWeight="medium"
                          sx={{
                            fontSize: "14px",
                            lineHeight: 1.2,
                            color: isActive
                              ? NOTIFICATION_COLORS.TAB_ACTIVE_TEXT
                              : NOTIFICATION_COLORS.TAB_INACTIVE_TEXT,
                            textTransform: "none",
                          }}
                        >
                          {tab.label}
                        </Text>
                        {count > 0 && (
                          <Chip
                            size="small"
                            label={count}
                            sx={{
                              height: "20px",
                              minWidth: "20px",
                              borderRadius: "16px",
                              fontSize: "12px",
                              fontWeight: 500,
                              lineHeight: 1.2,
                              px: "8px",
                              "& .MuiChip-label": {
                                px: 0,
                              },
                              backgroundColor: isActive
                                ? NOTIFICATION_COLORS.TAB_ACTIVE_BADGE_BG
                                : NOTIFICATION_COLORS.TAB_INACTIVE_BADGE_BG,
                              color: isActive
                                ? NOTIFICATION_COLORS.TAB_ACTIVE_BADGE_TEXT
                                : NOTIFICATION_COLORS.TAB_INACTIVE_BADGE_TEXT,
                            }}
                          />
                        )}
                      </Flex>
                    }
                  />
                );
              })}
            </Tabs>
          </Box>

          {/* Content — p:16, gap:24 between groups */}
          <ScrollArea
            height="calc(100vh - 140px)"
            onScroll={(e: any) => fetchMoreOnBottomReached(e.target)}
          >
            <Box sx={{ p: "16px" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "24px",
                }}
              >
                {groupedNotifications.map(({ label, items }) => (
                  <Box
                    key={label}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    {/* Date label — 14px, medium, #21262B */}
                    <Text
                      fontWeight="medium"
                      sx={{
                        fontSize: "14px",
                        lineHeight: 1.2,
                        color: NOTIFICATION_COLORS.DATE_LABEL_TEXT,
                      }}
                    >
                      {label}
                    </Text>

                    {items.map((it) => (
                      <NotificationCard
                        key={it?.uuid}
                        notifyData={it}
                        onRead={() =>
                          onRead(
                            it.uuid,
                            notifications.findIndex((n) => n.uuid === it.uuid)
                          )
                        }
                      />
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>
            {isFetching && (
              <Flex width="100%" justifyContent="center" py={2}>
                <CircularProgress size={24} />
              </Flex>
            )}
          </ScrollArea>
        </Box>
      </SideSheetDrawer>
    </>
  );
};

export default PatientPortalNotification;

// --- Card ---

const NotificationCard = ({
  notifyData,
  onRead,
}: {
  notifyData: any;
  onRead: () => void;
}) => {
  const isUnread = !notifyData?.read;
  const category = notifyData?.category ?? "";
  const Icon = CATEGORY_ICON_MAP[category] ?? NotificationsNoneRoundedIcon;

  return (
    <Box
      onClick={() => (isUnread ? onRead() : undefined)}
      sx={{
        display: "flex",
        gap: "12px",
        alignItems: isUnread ? "center" : "flex-start",
        p: "8px",
        borderRadius: "4px",
        cursor: isUnread ? "pointer" : "default",
        // Unread: bg #F2F6F8, border #E3ECEF
        // Read: bg #FFFFFF, border #E7E7E7
        backgroundColor: isUnread
          ? NOTIFICATION_COLORS.CARD_UNREAD_BG
          : NOTIFICATION_COLORS.CARD_READ_BG,
        border: isUnread
          ? `1px solid ${NOTIFICATION_COLORS.CARD_UNREAD_BORDER}`
          : `1px solid ${NOTIFICATION_COLORS.CARD_READ_BORDER}`,
      }}
    >
      {/* Icon — 32x32 circle, bg #E5F3FF */}
      <Box
        sx={{
          width: 32,
          height: 32,
          minWidth: 32,
          borderRadius: "100px",
          backgroundColor: NOTIFICATION_COLORS.ICON_CIRCLE_BG,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 16, color: NOTIFICATION_COLORS.ICON_COLOR }} />
      </Box>

      {/* Text + time row */}
      <Box
        sx={{
          display: "flex",
          flex: 1,
          alignItems: isUnread ? "center" : "flex-start",
          justifyContent: "space-between",
          gap: "8px",
          minWidth: 0,
        }}
      >
        {/* Title — 14px, medium (unread) / regular (read), #373D41, lineHeight 1.6 */}
        <Box
          sx={{
            flex: 1,
            fontSize: "14px",
            fontWeight: isUnread ? 500 : 400,
            lineHeight: 1.6,
            color: NOTIFICATION_COLORS.CARD_TITLE_TEXT,
            minWidth: 0,
          }}
        >
          {notifyData?.title}
        </Box>

        {/* Time + dot */}
        <Box
          sx={{
            display: "flex",
            gap: "6px",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              fontSize: "12px",
              fontWeight: 400,
              lineHeight: 1.2,
              color: NOTIFICATION_COLORS.CARD_TIME_TEXT,
              whiteSpace: "nowrap",
              textAlign: "center",
            }}
          >
            {notifyData?.createdAt ? formatTimeAgo(notifyData.createdAt) : ""}
          </Box>
          {isUnread && (
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "100px",
                backgroundColor: NOTIFICATION_COLORS.CARD_UNREAD_DOT,
                flexShrink: 0,
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

// --- Helpers ---

function groupByDate(notifications: any[]) {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const groups: Record<string, any[]> = {};

  notifications.forEach((n) => {
    const date = new Date(n?.createdAt);
    let label: string;

    if (isSameDay(date, today)) {
      label = "Today";
    } else if (isSameDay(date, yesterday)) {
      label = "Yesterday";
    } else {
      const dayName = date.toLocaleDateString("en-US", { weekday: "long" });
      const formatted = formatDate(n?.createdAt) ?? "";
      label = `${dayName}, ${formatted}`;
    }

    if (!groups[label]) groups[label] = [];
    groups[label].push(n);
  });

  return Object.entries(groups).map(([label, items]) => ({ label, items }));
}
